import { useState, useRef, useEffect } from 'react';
import { useAuthStore } from '@/context/authStore';
import { useTenantProfile, useUpdateTenantProfile, useUploadTenantLogo, useDeleteTenantLogo } from '@/hooks/useTenant';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import {
  Building2,
  MapPin,
  Phone,
  Mail,
  Camera,
  Trash2,
  Save,
  Settings,
  Loader2,
  X,
} from 'lucide-react';

export default function StoreProfile() {
  const { user } = useAuthStore();
  const { data: tenant, isLoading } = useTenantProfile();
  const updateProfile = useUpdateTenantProfile();
  const uploadLogo = useUploadTenantLogo();
  const deleteLogo = useDeleteTenantLogo();

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    cuit: '',
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const isAdmin = user?.role === 'admin';

  // Config de la tienda online
  const [storeForm, setStoreForm] = useState({ store_whatsapp: '', store_transfer_info: '', store_description: '' });
  useEffect(() => {
    if (tenant) {
      setStoreForm({
        store_whatsapp: tenant.store_whatsapp || '',
        store_transfer_info: tenant.store_transfer_info || '',
        store_description: tenant.store_description || '',
      });
    }
  }, [tenant?.id]);

  const handleSaveStore = async () => {
    await updateProfile.mutateAsync(storeForm);
  };

  // Inicializar form cuando llega el tenant
  const initForm = () => {
    if (tenant) {
      setFormData({
        name: tenant.name || '',
        address: tenant.address || '',
        phone: tenant.phone || '',
        cuit: tenant.cuit || '',
      });
    }
  };

  const handleEdit = () => {
    initForm();
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleSave = async () => {
    await updateProfile.mutateAsync(formData);
    setIsEditing(false);
  };

  const handleLogoClick = () => {
    if (isAdmin && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        await uploadLogo.mutateAsync(file);
      } finally {
        // Resetear el input para permitir subir el mismo archivo nuevamente
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    }
  };

  const handleDeleteLogo = async () => {
    if (confirm('¿Eliminar el logo del almacén?')) {
      await deleteLogo.mutateAsync();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="text-center py-8 text-gray-500">
        No se pudo cargar la informacion del almacén
      </div>
    );
  }

  const logoUrl = tenant.logo_url
    ? `${import.meta.env.VITE_API_URL?.replace('/api/v1', '') || ''}${tenant.logo_url}`
    : null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Perfil del Almacén</h1>
        <p className="text-gray-600">Informacion y configuracion de tu almacén</p>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        {/* Card del Logo */}
        <div className="card">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Logo</h2>
            <p className="text-sm text-gray-500">Imagen de tu almacén</p>
          </div>

          <div className="flex flex-col items-center gap-4">
            <div
              className={`relative ${isAdmin ? 'cursor-pointer group' : ''}`}
              onClick={handleLogoClick}
            >
              {logoUrl ? (
                <div className="h-32 w-32 rounded-full overflow-hidden border-4 border-gray-100">
                  <img
                    src={logoUrl}
                    alt={tenant.name}
                    className="h-full w-full object-cover"
                  />
                </div>
              ) : (
                <div className="h-32 w-32 rounded-full bg-primary-100 flex items-center justify-center">
                  <Building2 className="h-12 w-12 text-primary-600" />
                </div>
              )}
              {isAdmin && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                  <Camera className="h-8 w-8 text-white" />
                </div>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleLogoChange}
            />

            {isAdmin && tenant.logo_url && (
              <Button
                variant="secondary"
                size="sm"
                onClick={handleDeleteLogo}
                loading={deleteLogo.isPending}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Eliminar logo
              </Button>
            )}

            {uploadLogo.isPending && (
              <p className="text-sm text-gray-500">Subiendo...</p>
            )}
          </div>
        </div>

        {/* Card de Informacion */}
        <div className="card md:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Informacion General</h2>
              <p className="text-sm text-gray-500">Datos de contacto del almacén</p>
            </div>
            {isAdmin && !isEditing && (
              <Button variant="secondary" onClick={handleEdit}>
                <Settings className="h-4 w-4 mr-2" />
                Editar
              </Button>
            )}
          </div>

          {isEditing ? (
            <div className="space-y-4">
              <Input
                label="Nombre del Almacén"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ej: Almacén Central"
              />

              <Input
                label="Direccion"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Ej: Av. Corrientes 1234, CABA"
              />

              <Input
                label="Telefono"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="Ej: 11-1234-5678"
              />

              <Input
                label="CUIT (para el ticket)"
                value={formData.cuit}
                onChange={(e) => setFormData({ ...formData, cuit: e.target.value })}
                placeholder="Ej: 27-31254024-3"
              />

              <div className="flex gap-2 pt-4 border-t">
                <Button
                  onClick={handleSave}
                  loading={updateProfile.isPending}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Guardar
                </Button>
                <Button variant="secondary" onClick={handleCancel}>
                  <X className="h-4 w-4 mr-2" />
                  Cancelar
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Building2 className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="font-medium text-gray-900">{tenant.name}</p>
                  <p className="text-sm text-gray-500">Nombre</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <MapPin className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="font-medium text-gray-900">{tenant.address || 'No especificada'}</p>
                  <p className="text-sm text-gray-500">Direccion</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="font-medium text-gray-900">{tenant.phone || 'No especificado'}</p>
                  <p className="text-sm text-gray-500">Telefono</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="font-medium text-gray-900">{tenant.email || 'No especificado'}</p>
                  <p className="text-sm text-gray-500">Email</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Funciones */}
      {isAdmin && (
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Funciones</h2>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="font-medium text-gray-900">Combos / Promociones</p>
              <p className="text-sm text-gray-500">
                Permite crear combos con precio promocional y armarlos en la venta.
              </p>
            </div>
            <button
              type="button"
              onClick={() => updateProfile.mutate({ combos_enabled: !tenant.combos_enabled })}
              disabled={updateProfile.isPending}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                tenant.combos_enabled ? 'bg-purple-600' : 'bg-gray-300'
              }`}
              role="switch"
              aria-checked={tenant.combos_enabled}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                  tenant.combos_enabled ? 'translate-x-5' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>
        </div>
      )}

      {/* Tienda online */}
      {isAdmin && (
        <div className="card">
          <div className="flex items-center justify-between gap-4 mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Tienda online</h2>
            <button
              type="button"
              onClick={() => updateProfile.mutate({ online_store_enabled: !tenant.online_store_enabled })}
              disabled={updateProfile.isPending}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                tenant.online_store_enabled ? 'bg-emerald-600' : 'bg-gray-300'
              }`}
              role="switch"
              aria-checked={tenant.online_store_enabled || false}
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                tenant.online_store_enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
          </div>

          {tenant.online_store_enabled ? (
            <div className="space-y-4">
              {/* Segundo paso: habilitada NO es lo mismo que publicada. Se habilita
                  para prepararla con calma (fotos, precios, datos) y se publica
                  cuando esta lista. Antes un solo interruptor hacia las dos cosas y
                  no habia forma de armarla sin exponerla a medio hacer. */}
              {tenant.store_published ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-sm">
                  <span className="text-emerald-800 font-medium">Tu tienda está publicada.</span>{' '}
                  <span className="text-emerald-700">Link: </span>
                  <code className="text-emerald-900 break-all">tienda.animastore.com.ar/{tenant.slug}</code>{' '}
                  <a
                    href={`/tienda/${tenant.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-emerald-900 font-medium underline underline-offset-2 hover:text-emerald-700"
                  >
                    abrir
                  </a>
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-3 text-sm">
                  <p className="text-amber-900 font-medium">
                    Tu tienda está en preparación: todavía no la ve nadie.
                  </p>
                  <p className="text-amber-800 mt-1">
                    Cargá las fotos, los precios y los datos con tranquilidad. Cuando
                    esté lista, publicala con el botón de abajo y recién ahí queda
                    visible en <code className="break-all">tienda.animastore.com.ar/{tenant.slug}</code>.
                  </p>
                  <a
                    href={`/tienda/${tenant.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 mt-2 text-amber-900 font-medium underline underline-offset-2 hover:text-amber-700"
                  >
                    Ver cómo va quedando
                  </a>
                </div>
              )}

              <div className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 px-3 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900">
                    {tenant.store_published ? 'Visible al público' : 'Publicar la tienda'}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {tenant.store_published
                      ? 'Cualquiera con el link puede entrar y hacer pedidos.'
                      : 'Mientras esté sin publicar, el link no abre para nadie.'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => updateProfile.mutate({ store_published: !tenant.store_published })}
                  disabled={updateProfile.isPending}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                    tenant.store_published ? 'bg-emerald-600' : 'bg-gray-300'
                  }`}
                  role="switch"
                  aria-checked={tenant.store_published || false}
                >
                  <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                    tenant.store_published ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp para pedidos</label>
                <input
                  type="text"
                  value={storeForm.store_whatsapp}
                  onChange={(e) => setStoreForm({ ...storeForm, store_whatsapp: e.target.value })}
                  placeholder="Ej: 5493815791046 (con código de país, sin + ni espacios)"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Datos de transferencia</label>
                <textarea
                  value={storeForm.store_transfer_info}
                  onChange={(e) => setStoreForm({ ...storeForm, store_transfer_info: e.target.value })}
                  rows={3}
                  placeholder={'Ej:\nBanco Nación · Alias: mi.alias · CBU 0000...\nTitular: Juan Pérez'}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descripción de la tienda (opcional)</label>
                <textarea
                  value={storeForm.store_description}
                  onChange={(e) => setStoreForm({ ...storeForm, store_description: e.target.value })}
                  rows={2}
                  placeholder="Una frase que se muestra en el encabezado de tu tienda"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                />
              </div>

              <div className="flex justify-end">
                <Button onClick={handleSaveStore} loading={updateProfile.isPending}>
                  Guardar datos de la tienda
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500">
              Activá la tienda para publicar tu catálogo online. Tus clientes verán tus productos con foto y precio,
              y te hacen el pedido por WhatsApp (pago por transferencia o efectivo).
            </p>
          )}
        </div>
      )}

      {/* Info adicional */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Informacion del Sistema</h2>

        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <p className="text-sm text-gray-500">Identificador</p>
            <p className="font-mono text-sm text-gray-900">{tenant.slug}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Creada el</p>
            <p className="text-sm text-gray-900">
              {new Date(tenant.created_at).toLocaleDateString('es-AR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Estado</p>
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
              tenant.is_active
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            }`}>
              {tenant.is_active ? 'Activa' : 'Inactiva'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
